import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../components/Seo";
import { api } from "../lib/api";
import { ArrowLeft } from "lucide-react";

export default function NewsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/news/${slug}`).then(({ data }) => setArticle(data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="py-32 text-center">
        <Seo title="Article not found" path={`/news/${slug}`} />
        <h1 className="font-serif text-4xl text-forest">Article not found</h1>
        <p className="mt-4 text-charcoal/70">This article may have been unpublished.</p>
        <Link to="/news" className="mt-6 inline-block text-forest underline">Back to News &amp; Stories</Link>
      </div>
    );
  }
  if (!article) return <div className="py-32 text-center text-charcoal/60">Loading…</div>;

  return (
    <article>
      <Seo
        title={article.seo_title || article.title}
        path={`/news/${article.slug}`}
        description={article.seo_description || article.summary}
        type="article"
      />
      <header className="bg-sandstone/40 border-b border-sandstone">
        <div className="max-w-3xl mx-auto px-6 py-20 lg:py-28">
          <Link to="/news" className="inline-flex items-center gap-2 text-sm text-forest hover:gap-3 transition-[gap] duration-300" data-testid="article-back-link">
            <ArrowLeft size={15} aria-hidden="true" /> All news &amp; stories
          </Link>
          <p className="mt-8 text-xs tracking-[0.25em] uppercase font-semibold text-gold">{article.category}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-medium text-charcoal tracking-tight leading-tight">
            {article.title}
          </h1>
          {article.published_at && (
            <p className="mt-5 text-sm text-charcoal/60">
              Published {new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
        </div>
      </header>
      {article.cover_image && (
        <div className="max-w-4xl mx-auto px-6 -mt-0 py-10">
          <img src={article.cover_image} alt="" className="w-full aspect-[16/9] object-cover border border-sandstone" />
        </div>
      )}
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="space-y-6 text-charcoal/85 leading-relaxed text-lg" data-testid="article-content">
          {String(article.content || "").split(/\n{2,}/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </div>
    </article>
  );
}
